import {type PropsWithChildren, createContext, useContext, useMemo} from 'react'
import {type HeaderTags, defaultTitle, defaultTitleHeaderTag} from '../constants'

export type HeaderTag = (typeof HeaderTags)[number]

export type TitleContextProps = {
  /**
   * A title provides the ListView with a concise, descriptive name that communicates what kind of content it
   * contains. Will be visually hidden.
   */
  title: string

  /**
   * The HTML element used for the visually hidden title. Should change depending on the information hierarchy of the
   * page where the ListView is used.
   */
  titleHeaderTag: HeaderTag
}

const TitleContext = createContext<TitleContextProps | undefined>(undefined)

export type TitleProviderProps = PropsWithChildren<{
  /**
   * The HTML element used for the visually hidden title. Should change depending on the information hierarchy of the
   * page where the ListView is used. Defaults to an `<h2>` tag.
   */
  titleHeaderTag?: TitleContextProps['titleHeaderTag']
}> &
  Pick<TitleContextProps, 'title'>

export const TitleProvider = ({children, title, titleHeaderTag = defaultTitleHeaderTag}: TitleProviderProps) => {
  const contextProps = useMemo(
    () => ({title: title.trim() || defaultTitle, titleHeaderTag}) satisfies TitleContextProps,
    [title, titleHeaderTag],
  )
  return <TitleContext.Provider value={contextProps}>{children}</TitleContext.Provider>
}

export const useListViewTitle = () => {
  const context = useContext(TitleContext)
  if (!context) throw new Error('useListViewTitle must be used with TitleProvider.')
  return context
}

try{ TitleContext.displayName ||= 'TitleContext' } catch {}
try{ TitleProvider.displayName ||= 'TitleProvider' } catch {}